<script setup>
import AdminLayout from "../Layouts/AdminLayout.vue";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Dropdown from "primevue/dropdown";
import Tag from "primevue/tag";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import { ref, computed } from "vue";
import { useToast } from "primevue/usetoast";
import { Inertia } from "@inertiajs/inertia";
import ComposeMessageDialog from "../Components/ComposeMessageDialog.vue";

const props = defineProps({
    customers: {
        type: Array,
        default: () => []
    },
    filter: {
        type: String,
        default: 'this_month'
    },
    search: {
        type: String,
        default: ""
    },
    isSuperAdmin: {
        type: Boolean,
        default: false
    },
    companyOptions: {
        type: Array,
        default: () => []
    },
    filters: {
        type: Object,
        default: () => ({
            company_id: null
        })
    }
});

const toast = useToast();
const customerSearch = ref(props.search);
const selectedFilter = ref(props.filter);
const selectedCompany = ref(props.filters.company_id);
const showComposeDialog = ref(false);
const selectedRecipients = ref([]);

const filterOptions = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'this_week' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Next 30 Days', value: 'upcoming_30_days' }
];

const activeIndex = ref(1); // Set Birthdays tab as active

const handleTabChange = (event) => {
    // Index 0: Customers
    // Index 1: Birthdays
    // Index 2: History Log
    
    if (event.index === 0) {
        Inertia.visit('/admin/notifications');
    } else if (event.index === 2) {
        Inertia.visit('/admin/notifications/history');
    }
};

const handleFilterChange = () => {
    const params = {
        filter: selectedFilter.value,
    };

    if (props.isSuperAdmin) {
        params.company_id = selectedCompany.value;
    }

    Inertia.get('/admin/notifications/birthdays', params, {
        preserveState: true,
        preserveScroll: true
    });
};

const filteredCustomers = computed(() => {
    const searchVal = customerSearch.value ? customerSearch.value.trim() : "";
    if (!searchVal) return props.customers;
    
    const searchLower = searchVal.toLowerCase();
    return props.customers.filter(customer => 
        (customer.name && customer.name.toLowerCase().includes(searchLower)) ||
        (customer.email && customer.email.toLowerCase().includes(searchLower))
    );
});



const handleSendToIndividual = (customer) => {
    selectedRecipients.value = [{
        id: customer.id,
        name: customer.name,
        email: customer.email
    }];
    showComposeDialog.value = true;
};

const handleMessageSent = () => {
    showComposeDialog.value = false;
    selectedRecipients.value = [];
};
</script>

<template>
    <AdminLayout>
        <div class="p-6">
            <div class="flex justify-between items-center mb-6">
                <h1 class="text-2xl font-semibold">Notifications Dashboard</h1>
            </div>

            <TabView :activeIndex="activeIndex" @tab-change="handleTabChange">
                <TabPanel header="Customers">
                    <!-- This tab will redirect to main page when clicked -->
                </TabPanel>

                <TabPanel header="Birthdays">
                    <div class="mb-4 flex gap-4 items-end">
                        <div class="flex-grow">
                            <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Suche</label>
                            <IconField iconPosition="left" class="w-full">
                                <InputIcon class="pi pi-search" />
                                <InputText 
                                    v-model="customerSearch" 
                                    placeholder="Search customers..." 
                                    class="w-full !h-[42px]"
                                />
                            </IconField>
                        </div>
                        
                        <div class="w-56 shrink-0">
                            <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Zeitraum</label>
                            <Dropdown 
                                v-model="selectedFilter" 
                                :options="filterOptions" 
                                optionLabel="label" 
                                optionValue="value"
                                @change="handleFilterChange"
                                class="w-full !h-[42px] flex items-center"
                            />
                        </div>

                        <div v-if="isSuperAdmin" class="w-64 shrink-0">
                            <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Unternehmen</label>
                            <Dropdown
                                v-model="selectedCompany"
                                :options="companyOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Alle Unternehmen"
                                @change="handleFilterChange"
                                class="w-full !h-[42px] flex items-center"
                                showClear
                                filter
                            />
                        </div>
                    </div>

                    <DataTable 
                        :value="filteredCustomers" 
                        dataKey="id"
                        :rows="10"
                        :paginator="true"
                        responsiveLayout="scroll"
                        class="mt-4"
                    >
                        <Column field="name" header="Name">
                            <template #body="{ data }">
                                <div class="flex items-center gap-3">
                                    <div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                        <i class="pi pi-user text-gray-600"></i>
                                    </div>
                                    <div>
                                        <div class="font-medium">
                                            {{ data.name }}
                                            <Tag v-if="data.is_birthday_today" value="Birthday!" severity="success" class="ml-2" />
                                        </div>
                                        <div class="text-sm text-gray-500">{{ data.email }}</div>
                                    </div>
                                </div>
                            </template>
                        </Column>
                        <Column field="birthdate" header="Birthdate"></Column>
                        <Column field="age" header="Age"></Column>
                        <Column field="days_until_birthday" header="Days until Birthday">
                            <template #body="{ data }">
                                <span :class="{'font-bold text-green-600': data.days_until_birthday === 0}">
                                    {{ data.days_until_birthday === 0 ? 'Today!' : data.days_until_birthday }}
                                </span>
                            </template>
                        </Column>
                        <Column header="Action" style="width: 100px">
                            <template #body="{ data }">
                                <Button 
                                    label="Send" 
                                    size="small"
                                    icon="pi pi-envelope"
                                    @click="handleSendToIndividual(data)"
                                />
                            </template>
                        </Column>
                        <template #empty>
                            <div class="p-4 text-center text-gray-500">
                                No birthdays found.
                            </div>
                        </template>
                    </DataTable>
                </TabPanel>

                <TabPanel header="History Log">
                    <!-- This tab will redirect to history page when clicked -->
                </TabPanel>
            </TabView>
        </div>

        <ComposeMessageDialog 
            v-model:visible="showComposeDialog"
            :recipients="selectedRecipients"
            defaultTitle="Happy Birthday! 🎉"
            defaultSummary="Wishing you a wonderful birthday filled with joy and happiness."
            defaultMessage="Dear Customer,
Wishing you a very Happy Birthday! 🎂 May your day be filled with happiness, joy, and special moments.
Thank you for being a valued part of our community. Best wishes, Your Team"
            @sent="handleMessageSent"
        />
    </AdminLayout>
</template>
